import { Autocomplete, Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { BorderedFormGrid } from "../../../Forms/BorderedFormGrid";
import { textFieldDefaultProps } from "../../../Forms/TextFieldDefaultProps";
import { PlantDock } from "../../PlantDock";
import { TextFieldInput } from "../../../Forms/TextFieldInput";
import { halfHourIntervalsFromZeroTo24 } from "../../../Utils/timeslots";
import { UPPERCASE_INPUT } from "../../../Forms/formStyles";
import MdmAccordion from "../../../Layout/Accordion/MdmAccordion";
import { dockSchema } from "./dockSchema";
import { useAddDock } from "../../Hooks/useAddDock";
import { useUpdateDock } from "../../Hooks/useUpdateDock";
import { StyledIconButton } from "../../../Layout/Buttons/StyledIconButton";
import { useFetchDocksForPlant } from "../../Hooks/useFetchDocksForPlant";
import { resetFormStateAndKeepFormValues } from "../../../Forms/resetFormStateAndKeepFormValues";
import { isFormSubmitForbidden } from "../../../Forms/isFormSubmitForbidden";
import { ReadOnlyProps } from "../../../Project/EditProject/ReadOnlyProps";

type Props = {
  initialPlantDock: PlantDock;
  deleteDock: (dock: PlantDock) => void;
};

export const DockForm = ({ initialPlantDock, deleteDock, isReadOnly }: Props & ReadOnlyProps) => {
  const methods = useForm<PlantDock>({
    resolver: yupResolver(dockSchema),
    defaultValues: initialPlantDock,
    mode: "all",
    disabled: isReadOnly,
  });
  const {
    control,
    getValues,
    formState: { disabled: isFormDisabled },
  } = methods;

  const [expanded, setExpanded] = useState<boolean>(initialPlantDock.IsNewDock ?? false);
  const toggleExpanded = () => setExpanded((prev) => !prev);

  const { mutateAsync: addDock } = useAddDock();
  const { mutateAsync: updateDock } = useUpdateDock();
  const { refetch: refetchDocks } = useFetchDocksForPlant(initialPlantDock.PlantId);

  const saveDock = (dock: PlantDock) => {
    const save = dock.IsNewDock ? addDock(dock) : updateDock(dock);
    save.then(async () => {
      await refetchDocks();
      resetFormStateAndKeepFormValues(methods);
    });
  };

  const saveDockButton = (
    <StyledIconButton
      key="save"
      onClick={(e) => {
        e.stopPropagation();
        methods.handleSubmit((values: PlantDock) => saveDock(values))();
      }}
      disabled={isFormSubmitForbidden(methods.formState)}
    >
      <SaveIcon />
    </StyledIconButton>
  );

  const deleteDockButton = (
    <StyledIconButton
      key="delete"
      onClick={async (e) => {
        e.stopPropagation();
        await deleteDock(methods.getValues());
      }}
      disabled={isReadOnly}
    >
      <DeleteIcon />
    </StyledIconButton>
  );

  const isExistingDock = !methods.watch("IsNewDock");
  const controls = isExistingDock ? [saveDockButton, deleteDockButton] : [saveDockButton];

  return (
    <MdmAccordion
      title={isFormDisabled ? initialPlantDock?.Name : getValues("Name")}
      controls={controls}
      expanded={expanded}
      onChange={toggleExpanded}
    >
      <BorderedFormGrid>
        <Grid item xs={3}>
          <TextFieldInput
            control={control}
            label="Dock Code"
            name="Code"
            inputProps={UPPERCASE_INPUT}
            disabled={isExistingDock}
          />
        </Grid>
        <Grid item xs={3}>
          <TextFieldInput control={control} name="Name" label="Name" />
        </Grid>
        {/* Placeholder */}
        <Grid item xs={6} />
      </BorderedFormGrid>

      <BorderedFormGrid>
        <Grid item xs={3}>
          <TextFieldInput
            control={control}
            name="NumberOfTrucks"
            label="Number of Trucks"
            type="number"
          />
        </Grid>
        {/* Placeholder */}
        <Grid item xs={9} />

        <Grid item xs={3}>
          <Controller
            name="PerDefinedTime"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                onChange={(_, data) => {
                  field.onChange(data);
                }}
                options={halfHourIntervalsFromZeroTo24}
                size="small"
                disableClearable
                popupIcon={<AccessTimeIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...textFieldDefaultProps}
                    label="per (defined time)"
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            )}
          />
        </Grid>
        {/* Placeholder */}
        <Grid item xs={9} />
      </BorderedFormGrid>
    </MdmAccordion>
  );
};
